import React, { useContext, useEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate, useParams } from 'react-router';
import { LuChevronRight, LuChevronsLeft } from 'react-icons/lu';
import styled from 'styled-components';
import useWindowSize from '../hooks/useWindowSize';
import ContainerBackgroundWrapper from '../components/Backgrounds';
import { Apis } from '../util/Apis';
import CustomLoading from '../components/CustomLoading';
import useGet from '../hooks/useGet';
import Button from '../components/atoms/Buttons';
import { RankingTitle } from '../styledComponents/TorneoStyled';
import winnerSound from '../assets/sounds/winner.mp3';
import { isWinnerSoundMuted } from '../util/winnerSound';
import { SiteContext } from '../context/SiteContext';

const Screen = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  min-height: 0;
  padding: clamp(12px, 2vh, 24px) clamp(16px, 4vw, 64px);
  box-sizing: border-box;
  font-family: 'Open Sans', sans-serif;
  color: #fff8eb;
  & *, & *::before, & *::after { box-sizing: border-box; }
`;
const Heading = styled(RankingTitle)`
  flex-shrink: 0;
  font-size: clamp(28px, 4.8vh, 48px);
  letter-spacing: .08em;
  text-shadow: 0 2px 0 #6c340b, 0 0 18px #ffc34a30;
  @media (max-width: 600px) { font-size: clamp(24px, 7vw, 32px); }
`;
const Announcement = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(6px, 1vh, 12px);
  width: 100%;
  margin-bottom: clamp(16px, 2.5vh, 28px);
  text-align: center;
  text-transform: uppercase;
  .winner-name {
    margin: 0;
    max-width: 100%;
    color: #fff8eb;
    font-size: clamp(32px, min(4.4vw, 6.8vh), 76px);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: .01em;
    text-shadow: 0 3px 4px #45000099;
    overflow-wrap: anywhere;
    text-wrap: balance;
  }
  .winner-intro { margin: 0; color: #fff1d6; font-size: clamp(15px, 2.2vh, 24px); font-weight: 600; letter-spacing: .06em; line-height: 1.3; }
  .winner-label { font-size: clamp(28px, 5.4vh, 56px); letter-spacing: .03em; }
  @media (max-width: 600px) { .winner-name { font-size: clamp(24px, 6.5vw, 32px); } }
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 1280px);
  flex-shrink: 0;
  margin-block: auto;
`;
const Results = styled.div`
  width: 100%;
  flex-shrink: 0;
`;
const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: clamp(10px, 1.6vh, 18px);
`;
const WinnerRow = styled.li`
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr) minmax(90px, 140px);
  align-items: center;
  gap: clamp(14px, 2vw, 28px);
  padding: clamp(8px, 1vh, 12px) clamp(16px, 2vw, 32px);
  min-height: clamp(64px, 9vh, 96px);
  border: 3px solid;
  border-color: #fff0b2 #b18236 #c99d4b #ebcb83;
  border-radius: 18px;
  background: ${({ theme }) => theme.raffleItemBackground || theme.itemRankBackground || 'rgba(192, 0, 10, .4)'};
  box-shadow: inset 0 0 0 1px #7d4926, inset 0 5px 8px -3px #35000099, inset 0 -1px 0 #ffeabc99;
  font-size: clamp(20px, min(2.2vw, 3.4vh), 32px);
  font-weight: 800;
  line-height: 1.15;
  .name { text-transform: uppercase; overflow-wrap: anywhere; }
  .score { text-align: right; font-variant-numeric: tabular-nums; color: #ffe69a; }
  .rank { display: flex; justify-content: center; align-items: center; width: 48px; min-height: 48px; border-radius: 12px; background: #33000055; color: #ffe69a; font-size: 1.1em; box-shadow: inset 0 1px 1px #fff4cf55, 0 2px 5px #30000040; }
  &[data-rank='1'] .rank { background: linear-gradient(145deg,#fff4c9,#e3b34b 49%,#bd8126 51%,#f4d37c); color: #673900; }
  &[data-rank='2'] .rank { background: linear-gradient(145deg,#f9fcff,#bfcbd6 49%,#8798a9 51%,#e4eaf0); color: #374452; }
  &[data-rank='3'] .rank { background: linear-gradient(145deg,#ffe0c1,#c18a55 49%,#985b30 51%,#e9b17e); color: #542b13; }
  @media (max-width: 600px) { grid-template-columns: 32px minmax(0,1fr) 58px; padding: 14px 10px; gap: 10px; font-size: 16px; .rank { width: 32px; min-height: 38px; border-radius: 8px; } }
`;
const Navigation = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 10px;
  color: #ffe6b2;
  font-size: 13px;
  & > span { margin-right: auto; }
  button {
    display: inline-flex; align-items: center; gap: 5px;
    min-height: 36px; padding: 7px 10px;
    border: 1px solid transparent; border-radius: 8px;
    background: transparent; color: inherit;
    font: 600 13px 'Open Sans', sans-serif; cursor: pointer;
  }
  button:hover:not(:disabled) { background: #ffe69a12; border-color: #ffe69a55; }
  button:focus-visible { outline: 2px solid #fff1bb; outline-offset: 2px; }
  button:disabled { opacity: .4; cursor: default; }
  svg { width: 17px; height: 17px; }
  @media (pointer: coarse) { button { min-height: 44px; } }
`;
const Confirm = styled(Button)`
  min-width: min(100%, 240px);
  flex-shrink: 0;
  margin-top: clamp(12px, 2vh, 22px);
  @media (max-height: 800px) { min-height: 48px; padding: 8px 28px; font-size: 20px; }
`;
const Empty = styled.div`
  margin: auto;
  text-align: center;
  h1 { color: #ffe69a; font-size: clamp(24px, 3vw, 42px); }
  p { font-size: 18px; }
`;

const PAGE_SIZE = 3;

export default function TorneoWinner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const size = useWindowSize();
  const { salaId } = useContext(SiteContext);
  const [page, setPage] = useState(0);
  // Otro torneo vuelve a la primera página.
  const [pageFor, setPageFor] = useState(id);
  if (id !== pageFor) {
    setPageFor(id);
    setPage(0);
  }
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const { loading, data, error } = useGet({
    url: Apis.PC + '/coupon/group-by-customer?couponTypeId=' + id,
    initialValue: [],
  });
  const ranked = useMemo(() => (Array.isArray(data) ? data : [])
    .filter(item => Number.isFinite(Number(item.rank)) && Number(item.rank) > 0)
    .slice().sort((a, b) => Number(a.rank) - Number(b.rank)), [data]);
  const hasResults = ranked.length > 0;
  const winner = ranked.find(item => Number(item.rank) === 1);
  const pageCount = Math.ceil(ranked.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(0, pageCount - 1));
  const start = currentPage * PAGE_SIZE;
  const visible = ranked.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;
    const change = () => setReducedMotion(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    if (!hasResults || loading || error || isWinnerSoundMuted(salaId)) return undefined;
    const audio = new Audio(winnerSound);
    audio.play()?.catch(() => {});
    return () => audio.pause();
  }, [hasResults, loading, error, id, salaId]);

  return <ContainerBackgroundWrapper showBackgroundCard={false}>
    {!loading && !error && hasResults && !reducedMotion && <Confetti
      width={size.width} height={size.height} numberOfPieces={100}
      gravity={.065} wind={.015} opacity={.85}
      colors={['#ffd25a', '#f5b51b', '#fff0a4', '#c60019', '#f22b35']}
      style={{ pointerEvents: 'none', zIndex: 2 }} />}
    {loading ? <CustomLoading /> : <Screen className="custom-scroll">
      {error || !hasResults ? <Empty>
        <h1>{error ? 'No se pudieron cargar los resultados' : 'Aún no hay resultados'}</h1>
        <p>{error ? 'Vuelve a intentarlo en unos momentos.' : 'Los ganadores aparecerán cuando estén disponibles.'}</p>
        <Button variant="outline" onClick={() => navigate('/sorteo-list')}>INICIO</Button>
      </Empty> : <Content>
        <Announcement>
          <Heading>FELICIDADES</Heading>
          {winner && <>
            <h2 className="winner-name">{[winner.firstName, winner.lastName1].filter(Boolean).join(' ') || 'Participante'}</h2>
            <p className="winner-intro">ES NUESTRO(A)</p>
            <Heading as="p" className="winner-label">GANADOR(A)!</Heading>
          </>}
        </Announcement>
        {visible.length > 0 && <Results>
          <List aria-label="Ganadores del torneo" start={Number(visible[0].rank)}>
            {visible.map(item => <WinnerRow key={item.rank} data-rank={item.rank} value={Number(item.rank)}>
              <span className="rank" aria-label={'Puesto ' + item.rank}>{item.rank}</span>
              <span className="name">{[item.firstName, item.lastName1].filter(Boolean).join(' ') || 'Participante'}</span>
              <span className="score" aria-label={(item.total ?? 'Sin') + ' puntos'}>{item.total ?? '—'}</span>
            </WinnerRow>)}
          </List>
          {pageCount > 1 && <Navigation aria-label="Navegación entre ganadores">
            <span aria-live="polite">Puestos {visible[0].rank}–{visible[visible.length - 1].rank}</span>
            <button type="button" onClick={() => setPage(0)} disabled={currentPage === 0} aria-label="Volver a los primeros ganadores"><LuChevronsLeft aria-hidden="true" />Inicio</button>
            <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pageCount - 1} aria-label="Mostrar siguientes ganadores">Siguiente<LuChevronRight aria-hidden="true" /></button>
          </Navigation>}
        </Results>}
        <Confirm onClick={() => navigate('/sorteo-list')}>CONFIRMAR</Confirm>
      </Content>}
    </Screen>}
  </ContainerBackgroundWrapper>;
}
